import { v4 } from 'uuid';
import {
  ActorLogicFrom,
  ActorRef,
  Snapshot,
  assign,
  createActor,
  enqueueActions,
  fromPromise,
  setup,
  waitFor,
} from 'xstate';

import { scheduleBackgroundTaskAction } from '@eluve/appointment-tasks';
import { HttpError, NetworkError } from '@eluve/eluve-errors';
import { logAction } from '@eluve/frontend-machine-utils';

import { CallbackActorInvoke, UploadFileEvents } from './types';
import { UploadFileArgs } from './uploadFile.machine';

type NetworkRetryConfig = {
  retries: number;
  retryDelayMs: number;
};

export type Input = UploadFileArgs & {
  networkRetryConfig?: NetworkRetryConfig;
};

export type CheckFileOutput = {
  recordingDuration: number | null;
  fileHash: string;
};

export type GeneratePresignedUrlOutput = {
  isUploadRequired: boolean;
  preSignedUrl: string;
  fileUploadId: string;
  bytesUploaded?: number;
};

type Context = {
  uploadFileArgs: UploadFileArgs;
  preSignedUrl: string;
  checkFileOutput: CheckFileOutput;
  localUploadId: string;
  networkRetryConfig: NetworkRetryConfig;
  isUploadRequired: boolean;
  fileUploadId: string;
  bytesUploaded: number;
};

type Emitted = UploadFileEvents;

const checkFile = fromPromise<CheckFileOutput, UploadFileArgs>(
  async ({ input: file }) => {
    throw new Error('checkFile not implemented');
  },
);

const generatePresignedUrl = fromPromise<
  GeneratePresignedUrlOutput,
  UploadFileArgs & {
    checkFileOutput: CheckFileOutput;
    localUploadId: string;
    networkRetryConfig: NetworkRetryConfig;
  }
>(async () => {
  throw new Error('generatePresignedUrl not implemented');
});

const uploadFile = fromPromise<
  void,
  {
    fileUploadUrl: string;
    bytesUploaded?: number;
    mimeType: string;
    file: File;
    networkRetryConfig: NetworkRetryConfig;
    actorRef: ActorRef<Snapshot<unknown>, Emitted> | undefined;
  }
>(async () => {
  throw new Error('uploadFile not implemented');
});

const markFileComplete = fromPromise<
  void,
  {
    tenantId: string;
    appointmentId: string;
    recordingStartedAt: string;
    recordingDuration?: number | null;
    fileUploadId: string;
    shouldBeTranscribed: boolean;
    wasDegraded: boolean;
    chunkNum?: number | null;
  }
>(async () => {
  throw new Error('markFileComplete not implemented');
});

export const presignedUploadMachine = setup({
  types: {
    input: {} as Input,
    context: {} as Context,
    events: {} as Emitted,
    emitted: {} as Emitted,
  },
  actors: {
    checkFile,
    generatePresignedUrl,
    uploadFile,
    markFileComplete,
  },
  guards: {
    isUploadRequired: ({ context }) => context.isUploadRequired,
  },
  actions: {
    log: logAction,
    scheduleBackgroundTask: scheduleBackgroundTaskAction,
    emitFileUploadComplete: enqueueActions(({ enqueue, context }) => {
      enqueue.emit({
        type: 'UPLOAD_FILE.FILE_UPLOADED',
        fileAlreadyExistedOnServer: !context.isUploadRequired,
      });
    }),
    emitUploadProgress: enqueueActions(({ enqueue, event }) => {
      if (event.type === 'UPLOAD_FILE.UPLOADING') {
        enqueue.emit(event);
      }
    }),
    onError: enqueueActions(({ enqueue, event }) => {
      if ('error' in event && event.error) {
        enqueue.emit({
          type: 'UPLOAD_FILE.FAILED',
          error: event.error,
        });
      }
    }),
    trackUploadAttemptTag: enqueueActions(({ enqueue, context }) => {
      enqueue({
        type: 'scheduleBackgroundTask',
        params: {
          type: 'TASK.ADD_APPOINTMENT_TAG',
          appointmentId: context.uploadFileArgs.appointmentId,
          fileId: context.uploadFileArgs.fileId,
          tag: {
            type: 'SystemMetadata',
            key: 'FILE_UPLOAD_ATTEMPTED',
          },
          metadata: {
            uploadAttemptedAt: new Date().toISOString(),
          },
        },
      });
    }),

    scheduleUploadFailureTag: enqueueActions(({ enqueue, event, context }) => {
      const {
        uploadFileArgs: { appointmentId },
        fileUploadId,
      } = context;

      if ('error' in event && event.error) {
        const error = event.error;
        if (error instanceof NetworkError || error instanceof HttpError) {
          enqueue({
            type: 'scheduleBackgroundTask',
            params: {
              type: 'TASK.ADD_APPOINTMENT_TAG',
              appointmentId,
              tag: {
                type: 'SystemSelfResolvable',
                key: 'UPLOAD_FILE_NETWORK_FAILURE',
              },
              fileId: fileUploadId ?? undefined,
            },
          });
        }
      }
    }),
  },
}).createMachine({
  /** @xstate-layout N4IgpgJg5mDOIC5QAcBOcCWUB2kCqyANgPYCGEAsqQMYAWGuAxHgAoAyA8gIIAiA+gDEAkmwCiAOladeQgHIBxANoAGALqIUxWBgAuGYtg0gAHogAsARgvjlAVgDMDgEx2L9gGzuA7GYA0IAE9Ed3txd1srC2VPdycADi842wBfZP80TBx8IjJKGnpccQA1UkIMCFI9bCgBDEIwRggDMHEGADdiAGsWujBqTtr6lXUkEGQtXX1DUdMEey9rM2UnMwj3M3mvJ1t-IIQndzjxAE4ve3s4tzOE4-DU9PRtLIgCEnIqOgYWkrKKqpq6g0wKhUMRUOIiJUAGZggC24l6-UGYGGRnG2j0BiMs2U4nsFku9jsxziTiJFjMl12iDOoTc4R8Fm853s9zGjywuBeOXe+S+4nkYFwqEqDCgLA5zzwqEIjWarWwHW64hgwsqYAlmS50sIqNG6MmWJmiGOZmOJwstlN4XiDi21Lml3EUWOx3OTgsWzirrZGSe2p5eU+hUFav+mv9+BljGBoPBkJ0MNQ8NVwPVEc5Ud1ajRE0x01As1WXhOrtu9jMXmcTL8gRpF3EC1syzcdhCtjivslAbeQYKLVeuTFyPEAGFaH1OmKhFDB+RZGBIJBGHrNBiptjEBYXLiVttTXE7I4Ow71k5xGZ3ATlKcqx7lGYu1rsr2Pv3JIHh4CxxP+tPZ4GC5LhAK4WCMa6GgWJhbpEJy2C4cT2GStjOF4p63I2KGXB6jgUocT6Rtyr58oUc4QF+9Qfr2YpyoU7RdC0ACugbIquYx5huxoIBStgljeyjbq6V5mhSDpeMc1hbCylpeMoVYVgRmZEbkb78mRFEDp+1QxiCYIQoQ0JwuIzG9qxOb6hxRqFluDi4nEiExGYLieohDq3OalrHAJV4eBWcmKVKgaqYUVCoFO1RkaOxCwkQYA6A0TR0YqDHiLCpBhciUUxfU8VsQa+abtxZLmo4PgPscVptvYbleCW9kVuslKXDWAU9ipJEtKF4VQJF0WxfFOlxvphnJql6UDICWX9Si5kQQVXGutYOFIU4brKOtN5ue4HmVYh3oSZYXipGkIDYMQEBwGi3Yvu1wZgLm65WdBCAALSVmElxxNtSyettSQOp6JX4lWtXHAc8ROK1N28ndxSlOUorVMiD2QYVlYOgc7gXiS9gSYtuO2JWUPKTD76hmm4bXS8Moo-N1lzIkJw+BVSyOKSyw7HWjoltuBLxNEyxOPJxNkcFmnUUjgK05x9NJLYzouIc7gPvZ20OvB5orGsXkWGWCQi0FHVUUOkuUeOk7-mRQEXRA0tPbMXl0orX0q19xynrj4jNdhDiUiSnYnX6Smi0b6mm+LJtQHbUGzBSjjOrx4R81WcmnkyF5WIeSEEl4ngG8RsNdWKvXZXF90WY9MeIIh5qmrxyh7Z424OqS8urPizaXgeZiPoHVMh7DPDNNHhVRO9LheU5pwHqaAO65rcm3NsD5Wr3qRAA */
  id: 'presignedUploadMachine',
  context: ({ input }) => ({
    uploadFileArgs: input,
    preSignedUrl: '',
    checkFileOutput: { recordingDuration: null, fileHash: '' },
    localUploadId: v4(),
    fileUploadId: '',
    isUploadRequired: false,
    bytesUploaded: 0,
    networkRetryConfig: input.networkRetryConfig ?? {
      retries: 3,
      retryDelayMs: 5000,
    },
  }),
  initial: 'ValidatingFile',
  on: {
    'UPLOAD_FILE.UPLOADING': {
      actions: ['emitUploadProgress'],
    },
  },
  states: {
    ValidatingFile: {
      invoke: {
        id: 'checkFile',
        src: 'checkFile',
        input: ({ context }) => context.uploadFileArgs,
        onDone: {
          actions: [
            assign(({ event }) => ({
              checkFileOutput: event.output,
            })),
            {
              type: 'log',
              params: ({ event, context }) => ({
                level: 'info' as const,
                message: 'File validation complete',
                logMeta: {
                  fileHash: event.output.fileHash,
                  recordingDuration: event.output.recordingDuration,
                  fileId: context.uploadFileArgs.fileId,
                },
              }),
            },
          ],
          target: 'GeneratingPresignedUrl',
        },
        onError: {
          actions: ['onError'],
          target: 'Done',
        },
      },
    },
    GeneratingPresignedUrl: {
      invoke: {
        id: 'generatePresignedUrl',
        src: 'generatePresignedUrl',
        input: ({
          context: {
            uploadFileArgs,
            checkFileOutput,
            localUploadId,
            networkRetryConfig,
          },
        }) => {
          return {
            ...uploadFileArgs,
            checkFileOutput,
            localUploadId,
            networkRetryConfig,
          };
        },
        onDone: [
          {
            actions: [
              assign(({ event }) => ({
                preSignedUrl: event.output.preSignedUrl,
                isUploadRequired: event.output.isUploadRequired,
                fileUploadId: event.output.fileUploadId,
                bytesUploaded: event.output.bytesUploaded ?? 0,
              })),
              {
                type: 'log',
                params: ({ event, context }) => ({
                  level: 'info' as const,
                  message: event.output.isUploadRequired
                    ? 'Upload required, proceeding with file upload'
                    : 'File already exists on server (hash match), skipping upload',
                  logMeta: {
                    isUploadRequired: event.output.isUploadRequired,
                    fileUploadId: event.output.fileUploadId,
                    fileId: context.uploadFileArgs.fileId,
                    bytesUploaded: event.output.bytesUploaded ?? 0,
                  },
                }),
              },
            ],

            target: 'UploadingFile',
          },
        ],
        onError: {
          actions: ['onError'],
          target: 'Done',
        },
      },
    },
    UploadingFile: {
      entry: ['trackUploadAttemptTag'],
      initial: 'CheckingIfUploadNeeded',
      states: {
        CheckingIfUploadNeeded: {
          always: [
            {
              guard: 'isUploadRequired',
              target: 'Uploading',
            },
            { target: '#presignedUploadMachine.MarkingUploadComplete' },
          ],
        },
        Uploading: {
          invoke: {
            id: 'uploadFile',
            src: 'uploadFile',
            input: ({ context, self }) => {
              return {
                fileUploadUrl: context.preSignedUrl,
                bytesUploaded: context.bytesUploaded,
                mimeType: context.uploadFileArgs.format.mimeType,
                file: context.uploadFileArgs.file,
                networkRetryConfig: context.networkRetryConfig,
                actorRef: self,
              };
            },
            onDone: {
              target: '#presignedUploadMachine.MarkingUploadComplete',
            },
            onError: {
              actions: ['onError', 'scheduleUploadFailureTag'],
              target: '#presignedUploadMachine.Done',
            },
          },
        },
      },
    },
    MarkingUploadComplete: {
      entry: [
        {
          type: 'log',
          params: ({ context }) => ({
            level: 'info' as const,
            message: 'Marking file upload complete',
            logMeta: {
              fileUploadId: context.fileUploadId,
              fileId: context.uploadFileArgs.fileId,
              isUploadRequired: context.isUploadRequired,
            },
          }),
        },
      ],
      invoke: {
        id: 'markFileComplete',
        src: 'markFileComplete',
        input: ({
          context: {
            fileUploadId,
            checkFileOutput: { recordingDuration },
            uploadFileArgs: {
              appointmentId,
              tenantId,
              wasDegraded,
              chunkNum,
              shouldBeTranscribed,
              recordingStartedAt,
            },
          },
        }) => ({
          tenantId,
          appointmentId,
          shouldBeTranscribed,
          wasDegraded,
          chunkNum,
          recordingStartedAt,
          recordingDuration,
          fileUploadId,
        }),

        onDone: {
          target: 'Done',
          actions: ['emitFileUploadComplete'],
        },

        onError: {
          actions: ['onError'],
          target: 'Done',
        },
      },
    },
    Done: { type: 'final' },
  },
});

export const convertPresignedUrlMachineToCallback = (
  machineLogic: ActorLogicFrom<typeof presignedUploadMachine>,
) => {
  const wrapper: CallbackActorInvoke<Input, Emitted> = ({
    input,
    sendBack: parentSendback,
    emit,
  }) => {
    const actor = createActor(machineLogic, {
      input,
    });

    const onEvent = actor.on('*', (e) => {
      parentSendback(e);
      emit(e);
    });

    (async () => {
      try {
        actor.start();

        await waitFor(actor, (state) => state.status === 'done');
      } catch (e) {
        parentSendback({ type: 'UPLOAD_FILE.FAILED', error: e as Error });
      }
    })();

    return () => {
      onEvent.unsubscribe();
    };
  };

  return wrapper;
};
